import { type ApolloError, useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import dayjs from 'dayjs';
import type { NominationInformation } from '../../../../Assessment/graphql/DRCAssessment';
import { PAGE_TEXT } from '../../../components/lang';
import {
  type ScholarshipNominationMetadata,
  type ScholarshipNomination,
  type HistoryRecord,
  type MappingType,
  type FundingPanel,
  FundingSource,
  SYSTEM_SCORE,
  type DimensionData,
  DIMENSION_TYPE,
  type ScholarshipQuestionSetting,
  type ScholarshipQuestionData,
  type ScholarshipQuestion,
} from '../../../components';
import { checkInvalidDate } from '../../../../Application/graphql';
import {
  handleMapping,
  ROLES_SWITCH_BAR_TEXT,
  type UserRole,
  UserRoleToModuleMap,
} from '../../../../../components';
import { type APPLICATION_STATUS_TYPE } from '../../../../../utils';

const GET_NOMINATION_INFORMATION = gql`
  query ($filter: NominationFilter!) {
    getFundingSourceConfiguration {
      mapping
    }
    scholarshipNominationMetadata {
      academicExcellence {
        questionNumber
        mappedNumber
        questionTitle
        questionExample
        validUserRoles
        lengthLimit
        criteria
      }
      researchPotential {
        questionNumber
        mappedNumber
        questionTitle
        questionExample
        validUserRoles
        lengthLimit
        criteria
      }
      strengths {
        questionNumber
        mappedNumber
        questionTitle
        questionExample
        validUserRoles
        lengthLimit
        criteria
      }

      distinctiveQuality {
        questionNumber
        mappedNumber
        questionTitle
        questionExample
        validUserRoles
        lengthLimit
        criteria
      }

      dimensions {
        dimensionName
        abbv
        totalScore
        scoringSystems
        editableRoles
      }
    }
    nominations(filter: $filter) {
      _id
      application {
        _id
        academicReferees {
          selectedToHkpfs
        }
        status
      }
      admission {
        programme
      }
      fundings {
        schema
        source
        status
      }
      supervisors {
        netId
        givenName
        surname
        email
      }
      submission {
        submitted
      }
      shouldScholarshipNominationPerformed
      scholarshipNomination {
        polishStatus
        subjectClassification
        competitiveStudentship
        academicProgramme
        endorsement
        refereeReportChecked
        eligibilityCheck {
          funding
          checked
        }
        questions {
          questionNumber
          comment
          history {
            createdAt
            createdBy
            snapshot
            userRole
          }
        }

        scores {
          funding
          score
          outOfScore
          fundingAbbv
          fundingName
        }

        dimensionalScores {
          dimension
          systemScore
          overrideScore
          scoringSystem
          referenceScore
          referenceOverrideScore
          remark
          referenceRemark
          history {
            createdAt
            createdBy
            snapshot
            userRole
            justification
            candidate
          }
        }

        sectionalScores {
          dimension
          scoringSystem
          systemScore
          overrideScore
          lastUpdatedAt
          history {
            createdAt
            createdBy
            snapshot
            userRole
          }
        }

        justification
      }

      interviewPanel {
        scholarshipInterviewSubmission
        panelAgreement
        interviewLanguage
        interviewMode
        interviewDate
        panelMembers {
          netId
          title
          jobTitle
          givenName
          surname
          department
          institution
          telephoneNumber
          email
          isPanelChair
          rank
          post
        }
      }
    }
  }
`;

const panelValues = PAGE_TEXT.FUNDING_SOURCE.PANEL.VALUES;
const fundingSourceValues = PAGE_TEXT.FUNDING_SOURCE.FUNDING_SCHEME.VALUES;

export type ApplicationFilter = {
  application?: { _id: { eq: string } };
  _id?: { eq: string };
};

type GetNominationInformation = {
  nominationInformation: NominationInformation;
  scholarshipNominationMetadata: ScholarshipNominationMetadata;
  scholarshipNomination: ScholarshipNomination;
  isUPAF: boolean;
  isScholarshipInterviewFinished: boolean;
  hasDoneHKPFSRefereeSelection: boolean;
  status: APPLICATION_STATUS_TYPE;
  loading: boolean;
  error: ApolloError | undefined;
  refetch: () => void;
};

const processQuestionData = (
  questionMetaData: ScholarshipQuestionSetting[],
  questions: ScholarshipQuestion[],
): ScholarshipQuestionData[] => {
  return questionMetaData?.map(question => {
    const { mappedNumber, validUserRoles, lengthLimit, ...others } = question;
    const questionValue = questions.find(
      q => q.questionNumber === mappedNumber,
    ) ?? {
      comment: '',
      history: [],
    };

    return {
      ...others,
      comment: questionValue.comment,
      history: questionValue.history
        .sort((a, b) => dayjs(b.date).diff(dayjs(a.date)))
        .map(record => ({
          date: record.date,
          content: record.content
            .sort((a, b) =>
              dayjs(b.createdAt, 'HH:mm:ss').diff(
                dayjs(a.createdAt, 'HH:mm:ss'),
              ),
            )
            .map(record => {
              return {
                snapshot: record.snapshot,
                createdAt: record.createdAt,
                createdBy: record.createdBy,
                userRole:
                  ROLES_SWITCH_BAR_TEXT.NAMING[
                    handleMapping(
                      UserRoleToModuleMap[record.userRole as UserRole],
                    )
                  ] ?? '',
              };
            }),
        })),
      mappedNumber,
      lengthLimit,
      validUserRoles,
    };
  });
};

export const useGetNominationInformation = (
  filter: ApplicationFilter,
  skip?: boolean,
): GetNominationInformation => {
  const { data, loading, error, refetch } = useQuery(
    GET_NOMINATION_INFORMATION,
    {
      variables: { filter },
      fetchPolicy: 'no-cache',
      skip,
    },
  );
  const NA = skip ? 'No Permission' : 'N/A';

  const fundingsInfo = data?.nominations?.[0]?.fundings ?? [];

  const supervisorInfo = data?.nominations?.[0]?.supervisors ?? {};
  const rawStudyMode = data?.nominations?.[0]?.admission?.programme ?? '';

  const scholarshipNominationRawData =
    data?.nominations?.[0]?.scholarshipNomination ?? {};

  const panel =
    (scholarshipNominationRawData?.subjectClassification as FundingPanel) ?? NA;

  const competitiveStudentship: number | null =
    (scholarshipNominationRawData?.competitiveStudentship as number) ?? null;

  const processDimensionalScores = (
    scoringSystem: string,
    isCandidates: boolean,
  ): DimensionData[] => {
    // The order of displaying HKPFS and UPAF dimensions groups
    const HKPFSDimensionsOrder = [
      DIMENSION_TYPE.AE,
      DIMENSION_TYPE.RP,
      DIMENSION_TYPE.US,
      DIMENSION_TYPE.CD,
      DIMENSION_TYPE.LS,
      DIMENSION_TYPE.CI,
    ];

    const UPAFDimensionsOrder = [
      DIMENSION_TYPE.AE,
      DIMENSION_TYPE.RP,
      DIMENSION_TYPE.LS,
      DIMENSION_TYPE.CI,
      DIMENSION_TYPE.RE,
      DIMENSION_TYPE.OS,
      DIMENSION_TYPE.CD,
      DIMENSION_TYPE.US,
    ];

    return (data?.scholarshipNominationMetadata?.dimensions ?? [])
      .filter((dimension: any) => {
        return dimension?.scoringSystems?.includes(scoringSystem);
      })
      .sort((a: any, b: any) => {
        return scoringSystem === SYSTEM_SCORE.HKPFS
          ? HKPFSDimensionsOrder.indexOf(a.abbv) -
              HKPFSDimensionsOrder.indexOf(b.abbv)
          : UPAFDimensionsOrder.indexOf(a.abbv) -
              UPAFDimensionsOrder.indexOf(b.abbv);
      })
      .map((dimension: any) => {
        const { dimensionName, abbv, totalScore, editableRoles } = dimension;
        const {
          systemScore,
          overrideScore,
          history,
          referenceScore,
          referenceOverrideScore,
          referenceRemark,
          remark,
        } = scholarshipNominationRawData?.dimensionalScores?.find(
          (d: any) => d.dimension === abbv && d.scoringSystem === scoringSystem,
        ) ?? {
          systemScore: 0,
          overrideScore: 0,
          history: [],
          referenceScore: 0,
          referenceOverrideScore: 0,
          remark: '',
          referenceRemark: '',
        };

        return {
          dimensionName,
          remark: (isCandidates ? referenceRemark : remark) ?? '',
          dimension: abbv,
          scoringSystem,
          systemScore: Number(
            // never be empty and at least 0 from backend
            isCandidates ? referenceScore : systemScore,
          ).toFixed(0),
          overrideScore: Number(
            isCandidates
              ? (referenceOverrideScore as boolean)
                ? referenceOverrideScore
                : referenceScore
              : (overrideScore as boolean)
                ? overrideScore
                : systemScore,
          ).toFixed(0),
          totalScore,
          editableRoles,
          history:
            history?.map((record: any) => {
              return {
                createdAt: checkInvalidDate(record?.createdAt)
                  ? null
                  : record?.createdAt,
                createdBy: record?.createdBy ?? '',
                snapshot: record?.snapshot ?? '',
                userRole: record?.userRole ?? '',
                justification: record?.justification ?? '',
                candidate: record?.candidate ?? false,
              };
            }) ?? [],
        };
      });
  };

  const interviewPanelData = data?.nominations?.[0]?.interviewPanel ?? {};
  const isScholarshipInterviewFinished =
    interviewPanelData?.scholarshipInterviewSubmission ?? false;
  const checkedFunding = scholarshipNominationRawData?.eligibilityCheck ?? [];

  const competitiveFunding =
    data?.getFundingSourceConfiguration?.mapping?.COMPETITIVE ?? [];

  // Scholarship data initialization
  const questions: ScholarshipQuestion[] = (
    scholarshipNominationRawData?.questions ?? []
  ).map((question: any) => {
    const groupedRecords = (question?.history ?? []).reduce(
      (acc: any, record: any) => {
        const date = dayjs(record.createdAt).format('YYYY-MM-DD'); // it is required for the internal date result and this format is a must
        const time = dayjs(record.createdAt).format('HH:mm:ss');
        const formattedRecord = { ...record, createdAt: time };
        acc[date] = [...(acc[date] ?? []), formattedRecord];
        return acc;
      },
      {} as Record<string, HistoryRecord[]>,
    );

    const history = Object.entries(groupedRecords).map(([date, content]) => ({
      date,
      content,
    }));

    return {
      questionNumber: question.questionNumber,
      comment: question.comment,
      history,
    };
  }) as ScholarshipNomination['questions'];

  const scholarshipNomination: ScholarshipNomination = {
    polishStatus: scholarshipNominationRawData?.polishStatus ?? null,
    academicProgramme: scholarshipNominationRawData?.academicProgramme ?? '',
    endorsement: scholarshipNominationRawData?.endorsement ?? false,
    questions,
    scores: (scholarshipNominationRawData?.scores ?? []).map((score: any) => {
      return {
        occupied: Number(score.score).toFixed(2) ?? 0,
        total: score.outOfScore ?? 0,
        name: score?.fundingAbbv ?? score?.fundingName,
        funding: score?.funding ?? '',
      };
    }) as ScholarshipNomination['scores'],
    UPAFDimension: processDimensionalScores(
      SYSTEM_SCORE.UPAF,
      false,
    ) as ScholarshipNomination['UPAFDimension'],
    sectionalScores: scholarshipNominationRawData?.sectionalScores ?? [],
    HKPFSDimension: processDimensionalScores(
      SYSTEM_SCORE.HKPFS,
      false,
    ) as ScholarshipNomination['HKPFSDimension'],
    candidatesDimension: processDimensionalScores(SYSTEM_SCORE.UPAF, true),
    justification: scholarshipNominationRawData?.justification ?? '',
    interviewPanel: {
      panelAgreement: interviewPanelData?.panelAgreement ?? false,
      interviewLanguage: interviewPanelData?.interviewLanguage ?? '',
      interviewMode: interviewPanelData?.interviewMode ?? '',
      interviewDate: checkInvalidDate(interviewPanelData?.interviewDate)
        ? null
        : interviewPanelData?.interviewDate,
      panelMembers: (interviewPanelData?.panelMembers ?? []).map(
        (member: any) => {
          return {
            netId: member?.netId ?? '',
            title: member?.title ?? '',
            jobTitle: member?.jobTitle ?? '',
            givenName: member?.givenName ?? '',
            surname: member?.surname ?? '',
            institution: member?.institution ?? '',
            department: member?.department ?? '',
            email: member?.email ?? '',
            isPanelChair: member?.isPanelChair ?? false,
            post: member?.post ?? '',
            rank: member?.rank ?? '',
          };
        },
      ),
    },
    eligibilityCheck:
      (scholarshipNominationRawData?.scores ?? []).map((score: any) => {
        const { funding, fundingName } = score ?? {};
        const fundingDeadline = competitiveFunding.find(
          (item: any) => item?.value === funding,
        )?.recentOpeningPeriod?.to;
        const fundingStatus = fundingsInfo?.find((fundingItem: any) => {
          return fundingItem?.source === funding;
        })?.status;

        return {
          funding,
          fundingName,
          fundingDeadline,
          checked:
            checkedFunding?.find((checked: any) => checked?.funding === funding)
              ?.checked ?? false,
          status: fundingStatus,
        };
      }) ?? [],
    matchedQuestionsData: {
      academicExcellenceQuestions: processQuestionData(
        data?.scholarshipNominationMetadata?.academicExcellence,
        questions,
      ),
      researchPotentialQuestions: processQuestionData(
        data?.scholarshipNominationMetadata?.researchPotential,
        questions,
      ),
      strengthsQuestions: processQuestionData(
        data?.scholarshipNominationMetadata?.strengths,
        questions,
      ),
      distinctiveQualityQuestions: processQuestionData(
        data?.scholarshipNominationMetadata?.distinctiveQuality,
        questions,
      ),
    },
  };

  const studyMode: string =
    rawStudyMode.includes('FULL') === true ? 'Full Time' : 'Part Time/Online';

  const nominationInformation: NominationInformation = {
    applicationId: data?.nominations?.[0]?.application?._id ?? '',
    competitiveStudentship,
    panel: panelValues[panel as keyof typeof panelValues] ?? NA,
    schema: (fundingsInfo?.[0]?.schema as MappingType) ?? NA,
    fundingSchemes:
      (fundingsInfo ?? [])?.length === 0
        ? [NA]
        : fundingsInfo?.map(
            (funding: any) =>
              fundingSourceValues[
                funding?.source as keyof typeof fundingSourceValues
              ],
          ),
    chiefSupervisorInfo: {
      proposedChiefSupervisor:
        `${supervisorInfo?.surname?.toUpperCase() ?? NA},` +
        ' ' +
        `${supervisorInfo?.givenName ?? NA}`,
      chiefSupervisorNetID: supervisorInfo?.netId ?? NA,
      chiefSupervisorMail: supervisorInfo?.email ?? NA,
    },
    studyMode: rawStudyMode === '' ? '' : studyMode,
    recommendForHKPFS: fundingsInfo?.some(
      (funding: any) =>
        funding?.source === FundingSource.HONGKONG_PHD_FELLOWSHIP_SCHEME,
    ),
    submitted: data?.nominations?.[0]?.submission?.submitted ?? false,
  };

  const scholarshipNominationMetadata: ScholarshipNominationMetadata =
    data?.scholarshipNominationMetadata ?? {
      academicExcellence: [],
      researchPotential: [],
      strengths: [],
      distinctiveQuality: [],
    };
  const isUPAF: boolean =
    data?.nominations?.[0]?.shouldScholarshipNominationPerformed ?? false;

  const academicReferees =
    data?.nominations?.[0]?.application?.academicReferees ?? [];
  const hasDoneHKPFSRefereeSelection: boolean = academicReferees?.some(
    (ref: any) => ref.selectedToHkpfs,
  );
  const status = data?.nominations?.[0]?.application?.status ?? '';

  return {
    nominationInformation,
    scholarshipNominationMetadata,
    scholarshipNomination,
    isUPAF,
    isScholarshipInterviewFinished,
    hasDoneHKPFSRefereeSelection,
    status,
    loading,
    error,
    refetch,
  };
};
